import React from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash-es'
import { RootState } from '@store/store'
import { selectFieldSelectOptionsByFieldId } from '@reducers/data/field_select_option'
import { createLogger } from '@utils/logger'
import { useViewContext } from '@base/ViewContext'
import { getReadableTextColor } from '@helpers/color_scheme_helpers'
import { TableOptions } from '@app-types/general'

const log = createLogger('MultiSelectRenderer')
log.setLevel('info')

interface MultiSelectRendererProps {
  value?: number[] | null
  fieldId?: string
  onClick?: () => void
}

// Simple rendering without dynamic measurement: show up to a fixed number of chips,
// then a +N counter if there are more.

const MultiSelectRenderer: React.FC<MultiSelectRendererProps> & { RENDERER_TYPE: string } = props => {
  const { value, fieldId, onClick } = props

  if (!fieldId) {
    log.warn('No fieldId provided to MultiSelectRenderer. Unable to render value')
  }

  const viewContext = useViewContext()
  const tableOptions = get(viewContext, 'tableOptions', {}) as TableOptions

  const fieldSelectOptions = fieldId
    ? useSelector((state: RootState) => selectFieldSelectOptionsByFieldId(state, fieldId))
    : []

  const handleClick = (_e: React.MouseEvent<HTMLSpanElement>) => onClick?.()

  const filteredFieldSelectOptions =
    value && Array.isArray(value)
      ? fieldSelectOptions.filter(opt => {
          const idNum = parseInt(opt.id)
          return value.includes(idNum)
        })
      : []

  // Build chip nodes once (used by both measurer and visible row)
  const chipNodes = filteredFieldSelectOptions.map(opt => {
    let color: string | null = null

    if (tableOptions.grouping?.field_id && String(tableOptions.grouping.field_id) === String(opt.attributes.field_id)) {
      const optionIdStr = String(opt.id)
      const optionColor = tableOptions.grouping.colors ? tableOptions.grouping.colors[optionIdStr] : null
      if (optionColor) color = String(optionColor)
    }

    let chipStyle: React.CSSProperties | undefined = undefined
    let chipClassName = 'chip chip--small'
    if (color) {
      const readable = getReadableTextColor(color)
      chipStyle = { backgroundColor: color, color: readable }
    } else {
      chipClassName += ' chip--is-outlined'
    }

    return (
      <div key={opt.id} className={chipClassName} style={chipStyle}>
        <div className="chip-text" title={opt.attributes.label}>
          {opt.attributes.label}
        </div>
      </div>
    )
  })

  const MAX_VISIBLE_CHIPS = 3
  const visibleCount = Math.min(chipNodes.length, MAX_VISIBLE_CHIPS)
  const overflow = Math.max(0, chipNodes.length - visibleCount)

  const content = chipNodes.length ? (
    <div className="chips">
      {chipNodes.slice(0, visibleCount)}
      {overflow > 0 && (
        <span className="chip chip--small is-counter chip--is-outlined" onClick={handleClick} title="Show all">
          +{overflow}
        </span>
      )}
    </div>
  ) : null

  return (
    <span className="content-renderer content-renderer--multi-select multi-select-renderer" onClick={handleClick}>
      {content}
    </span>
  )
}

MultiSelectRenderer.RENDERER_TYPE = 'multi-select'
export default MultiSelectRenderer
